import type { AppointmentPatientPackageBillingIntent } from "@/hooks/useAppointmentPatientPackage";
import type { PatientPackageSummary } from "@/hooks/usePatientPackages";

type MinimalPackageItem = {
  id?: string | null;
  name?: string | null;
  sale_price?: number | null;
};

type MinimalCatalogPackage = {
  id?: string | null;
  controls_sessions?: boolean | null;
  item?: MinimalPackageItem | null;
};

type MinimalSearchPackageItem = {
  id: string;
  name: string;
  sale_price?: number | null;
};

type MinimalPatientPackage = {
  id?: string | null;
  full_price?: number | null;
  outstanding_amount?: number | null;
  catalog_package?: MinimalCatalogPackage | null;
  controls_sessions?: boolean | null;
};

export interface SuggestedPackageCharge {
  appointmentPatientPackageId?: string | null;
  patientPackageId?: string | null;
  catalogPackageId?: string | null;
  itemId?: string | null;
  petId?: string | null;
  packageMode?: "new" | "existing";
  title: string;
  fullPrice: number;
  outstandingAmount?: number;
  suggestedChargeAmount: number;
  billingIntent: AppointmentPatientPackageBillingIntent;
}

const normalizeCurrency = (value: number | null | undefined) => Math.max(0, Number(value ?? 0));

export const buildSuggestedPackageChargeFromAppointmentLink = (
  link: {
    id?: string | null;
    pet_id?: string | null;
    mode?: "new" | "existing" | null;
    billing_intent?: AppointmentPatientPackageBillingIntent | null;
    suggested_charge_amount?: number | null;
    catalog_package_id?: string | null;
    patient_package_id?: string | null;
    catalog_package?: MinimalCatalogPackage | null;
    patient_package?: MinimalPatientPackage | null;
  },
): SuggestedPackageCharge => {
  const patientPackageItem = link.patient_package?.catalog_package?.item;
  const catalogPackageItem = link.catalog_package?.item;
  const title = patientPackageItem?.name?.trim() || catalogPackageItem?.name?.trim() || "Pacote";
  const fullPrice = normalizeCurrency(
    link.patient_package?.full_price ?? patientPackageItem?.sale_price ?? catalogPackageItem?.sale_price,
  );
  const outstandingAmount = normalizeCurrency(link.patient_package?.outstanding_amount ?? fullPrice);

  return {
    appointmentPatientPackageId: link.id ?? null,
    patientPackageId: link.patient_package_id ?? link.patient_package?.id ?? null,
    catalogPackageId: link.catalog_package_id ?? link.catalog_package?.id ?? null,
    itemId: patientPackageItem?.id ?? catalogPackageItem?.id ?? null,
    petId: link.pet_id,
    packageMode: link.mode ?? undefined,
    title,
    fullPrice,
    outstandingAmount,
    suggestedChargeAmount: normalizeCurrency(link.suggested_charge_amount),
    billingIntent: link.billing_intent ?? "undecided",
  };
};

export const buildSuggestedPackageChargeFromPatientPackage = (
  pkg: Pick<
    PatientPackageSummary,
    | "id"
    | "pet_id"
    | "catalog_package_id"
    | "full_price"
    | "outstanding_amount"
    | "catalog_package"
  >,
): SuggestedPackageCharge => {
  const fullPrice = normalizeCurrency(pkg.full_price);
  const outstandingAmount = normalizeCurrency(pkg.outstanding_amount);
  const controlsSessions = pkg.catalog_package?.controls_sessions ?? true;

  return {
    appointmentPatientPackageId: null,
    patientPackageId: pkg.id,
    catalogPackageId: pkg.catalog_package_id,
    itemId: pkg.catalog_package?.item?.id ?? null,
    petId: pkg.pet_id,
    packageMode: "existing",
    title: pkg.catalog_package?.item?.name?.trim() || "Pacote",
    fullPrice,
    outstandingAmount,
    suggestedChargeAmount: outstandingAmount,
    billingIntent: !controlsSessions || outstandingAmount > 0 ? "integral_now" : "charge_later",
  };
};

export const buildSuggestedPackageChargeFromCatalogItem = (
  item: MinimalSearchPackageItem,
  options?: {
    catalogPackageId?: string | null;
    petId?: string | null;
  },
): SuggestedPackageCharge => {
  const fullPrice = normalizeCurrency(item.sale_price);

  return {
    appointmentPatientPackageId: null,
    patientPackageId: null,
    catalogPackageId: options?.catalogPackageId ?? null,
    itemId: item.id,
    petId: options?.petId ?? null,
    packageMode: "new",
    title: item.name.trim() || "Pacote",
    fullPrice,
    outstandingAmount: fullPrice,
    suggestedChargeAmount: fullPrice,
    billingIntent: "integral_now",
  };
};
